import React, { useEffect, useState } from 'react';
import { Box, FormControl, MenuItem, Paper, Select, Typography, CircularProgress } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { Client } from '@/types/client';
import { NutritionReportTable } from './analysis/NutritionReportTable';
import { buildAnalysisData, buildSubtitleParts } from './analysis/analysis.shared';
import { getDrvPrimaryTypes, calculateDrvRequirements } from '@/lib/api/drv';
import { getCountryCode } from '@/utils/country-code-mapping';
import { getViableFormulas } from '@/lib/api/clients';
import { DRVGuideline } from '@/types/calculation-formulas';
import { resolveClientTargetCalories } from '@/utils/client-goals';

interface ClientDetailsV2SuggestedRdaTabProps {
  client: Client;
  clientDisplayName: string;
}

const ENGINE_DRV_SOURCES = new Set([
  'iom_usa',
  'uk_sacn',
  'efsa_eu',
  'icmr_india',
  'ausnz_nrv',
  'nordic_nnr',
  'who_fao',
]);

const DRV_GUIDELINE_TO_SOURCE: Record<string, string> = {
  sacn_2017: 'uk_sacn',
  efsa_2017: 'efsa_eu',
  iom_dri: 'iom_usa',
  nrv_aus: 'ausnz_nrv',
  icmr_nin: 'icmr_india',
  nordic_nnr: 'nordic_nnr',
  who_fao: 'who_fao',
};

function normalizeDrvSourceSelection(value?: string | null): string {
  if (!value) return '';
  const normalized = value.trim().toLowerCase();
  if (!normalized) return '';
  if (ENGINE_DRV_SOURCES.has(normalized)) return normalized;
  return DRV_GUIDELINE_TO_SOURCE[normalized] || '';
}

function getClientDrvSource(client?: Client | null): string {
  return normalizeDrvSourceSelection(
    client?.drvRequirements?.source ||
    client?.drvSource ||
    client?.drvGuidelineId ||
    client?.drvFormulaId ||
    '',
  );
}

function getAge(dateOfBirth?: string | null): number | null {
  if (!dateOfBirth) return null;

  const dob = new Date(dateOfBirth);
  if (Number.isNaN(dob.getTime())) return null;

  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : null;
}

export const ClientDetailsV2SuggestedRdaTab: React.FC<ClientDetailsV2SuggestedRdaTabProps> = ({
  client,
  clientDisplayName,
}) => {
  const [primaryTypes, setPrimaryTypes] = React.useState<any[]>([]);
  const [drvGuidelines, setDrvGuidelines] = useState<DRVGuideline[]>([]);
  const [isLoadingGuidelines, setIsLoadingGuidelines] = useState(false);

  const clientDrvSource = React.useMemo(() => getClientDrvSource(client), [client]);
  const [selectedDrvSource, setSelectedDrvSource] = React.useState<string>(clientDrvSource);
  const lastClientSelectionRef = React.useRef({
    clientId: client.id,
    drvSource: clientDrvSource,
  });

  const [activeDrvRequirements, setActiveDrvRequirements] = useState<any>(client?.drvRequirements);
  const [isLoadingDrv, setIsLoadingDrv] = useState(false);

  useEffect(() => {
    if (!clientDrvSource) return;

    const last = lastClientSelectionRef.current;
    const clientChanged = last.clientId !== client.id;
    const sourceChanged = last.drvSource !== clientDrvSource;

    if (clientChanged || sourceChanged || !selectedDrvSource) {
      setSelectedDrvSource(clientDrvSource);
    }

    lastClientSelectionRef.current = {
      clientId: client.id,
      drvSource: clientDrvSource,
    };
  }, [client.id, clientDrvSource, selectedDrvSource]);

  useEffect(() => {
    const fetchGuidelines = async () => {
      setIsLoadingGuidelines(true);
      try {
        const age = getAge(client.dateOfBirth);
        const gender = client.gender?.trim().toLowerCase();
        if (age == null || !gender) {
          setDrvGuidelines([]);
          return;
        }
        const countryCode = client.location ? getCountryCode(client.location) : undefined;
        
        const response = await getViableFormulas({
          age,
          gender,
          countryCode,
        });

        const guidelines = response.availableDRVGuidelines || [];
        setDrvGuidelines(guidelines);
        
        const defaultSource = normalizeDrvSourceSelection(response.defaultDRVGuidelineId);
        const firstSource = normalizeDrvSourceSelection(guidelines[0]?.id);
        setSelectedDrvSource((current) => current || clientDrvSource || defaultSource || firstSource);
      } catch (error) {
        console.error('Failed to fetch DRV guidelines:', error);
      } finally {
        setIsLoadingGuidelines(false);
      }
    };

    fetchGuidelines();
  }, [client.dateOfBirth, client.gender, client.location, clientDrvSource]);

  useEffect(() => {
    const fetchPrimaryTypes = async () => {
      try {
        const source = selectedDrvSource || clientDrvSource || undefined;
        const response = await getDrvPrimaryTypes({ limit: 200, source });
        if (response && response.items) {
          setPrimaryTypes(response.items);
        }
      } catch (error) {
        console.error('Failed to fetch primary types:', error);
      }
    };
    fetchPrimaryTypes();
  }, [selectedDrvSource, clientDrvSource]);

  useEffect(() => {
    const fetchDrv = async () => {
      if (!selectedDrvSource) return;
      const weightKg = Number(client.weightKg);
      const eer = Number(resolveClientTargetCalories(client) ?? client.eerOverride ?? client.eerCalories ?? client.eer);
      if (
        !client.dateOfBirth
        || !client.gender
        || !Number.isFinite(weightKg)
        || weightKg <= 0
        || !Number.isFinite(eer)
        || eer <= 0
      ) {
        setActiveDrvRequirements(client?.drvRequirements);
        return;
      }

      setIsLoadingDrv(true);
      try {
        const payload: any = {
          dateOfBirth: client.dateOfBirth,
          gender: client.gender.toLowerCase(),
          eer: Math.round(eer),
          weightKg,
          drvSource: selectedDrvSource,
        };

        if ((client as any).sexAtBirth) {
          payload.sexAtBirth = (client as any).sexAtBirth;
        }
        if ((client as any).pregnancyTrimester && (client as any).pregnancyTrimester !== 'none') {
          payload.pregnancyTrimester = (client as any).pregnancyTrimester;
        }
        if ((client as any).lactationPeriod && (client as any).lactationPeriod !== 'none') {
          payload.lactationPeriod = (client as any).lactationPeriod;
        }
        if (client.location) {
          const code = getCountryCode(client.location);
          if (code) {
            payload.countryCode = code;
          }
        }

        const response = await calculateDrvRequirements(payload);
        if (response) {
          setActiveDrvRequirements(response.drvRequirements ?? response);
        }
      } catch (error) {
        console.error('Failed to fetch DRV requirements:', error);
        setActiveDrvRequirements(client?.drvRequirements);
      } finally {
        setIsLoadingDrv(false);
      }
    };

    fetchDrv();
  }, [
    selectedDrvSource,
    client.dateOfBirth,
    client.gender,
    client.eerOverride,
    client.eerCalories,
    client.eer,
    client.weightKg,
    client.drvFormulaId,
    client.drvGuidelineId,
    client.drvSource,
    (client as any).sexAtBirth,
    (client as any).pregnancyTrimester,
    (client as any).lactationPeriod,
    client.location,
    client.drvRequirements
  ]);

  const analysisData = React.useMemo(() => buildAnalysisData(client), [client]);
  const subtitleParts = React.useMemo(() => buildSubtitleParts(client, clientDisplayName), [client, clientDisplayName]);

  const age = getAge(client.dateOfBirth);
  const gender = client.gender ? client.gender.toLowerCase() : 'adult';
  const selectedGuidelineDetails = drvGuidelines.find(
    (guideline) => normalizeDrvSourceSelection(guideline.id) === selectedDrvSource,
  );

  return (
    <Box sx={{ mt: 2.25, display: 'grid', gap: 1.35, minWidth: 0 }}>
      <Box
        sx={{
          display: 'grid',
          gap: 1,
          gridTemplateColumns: {
            xs: '1fr',
            lg: '260px minmax(0, 1fr)',
          },
          alignItems: 'stretch',
        }}
      >
        <FormControl size="small" sx={{ width: '100%' }}>
          <Select
            value={selectedDrvSource}
            onChange={(event) => setSelectedDrvSource(event.target.value)}
            disabled={isLoadingGuidelines || drvGuidelines.length === 0}
            displayEmpty
            renderValue={(selected) => {
              if (isLoadingGuidelines) return "Loading guidelines...";
              if (drvGuidelines.length === 0) return "No guidelines available";
              
              if (selected === 'iom_usa') {
                return "Guideline: DRI / IOM";
              }
              const found = drvGuidelines.find(g => normalizeDrvSourceSelection(g.id) === selected);
              return found ? `Guideline: ${found.name}` : "";
            }}
            sx={{
              height: 44,
              borderRadius: '12px',
              bgcolor: '#fff',
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.82rem',
              fontWeight: 600,
              color: '#1D2939',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#D0D5DD',
              },
              '& .MuiSelect-select': {
                py: 0.9,
              },
            }}
          >
            {isLoadingGuidelines ? (
              <MenuItem disabled value="">
                <CircularProgress size={16} sx={{ mr: 1 }} /> Loading guidelines...
              </MenuItem>
            ) : drvGuidelines.length === 0 ? (
              <MenuItem disabled value="">
                No guidelines available
              </MenuItem>
            ) : (
              drvGuidelines.map((guideline) => (
                <MenuItem key={guideline.id} value={normalizeDrvSourceSelection(guideline.id)} sx={{ fontFamily: 'Inter, sans-serif', fontSize: '0.8rem' }}>
                  Guideline: {guideline.id === 'iom_usa' ? 'DRI / IOM' : guideline.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>

        <Paper
          elevation={0}
          sx={{
            border: '1px solid #B9E4F2',
            borderRadius: '12px',
            bgcolor: '#F3FBFE',
            px: 1.5,
            py: 1,
            display: 'flex',
            gap: 0.75,
            alignItems: 'center',
          }}
        >
          <InfoOutlinedIcon sx={{ color: '#20AEDC', fontSize: 18, flexShrink: 0 }} />
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '0.76rem',
              color: '#475467',
              lineHeight: 1.35,
            }}
          >
            Based on{' '}
            <Box component="span" sx={{ color: '#1FA6D3', fontWeight: 600 }}>
              {selectedDrvSource === 'iom_usa' ? 'DRI / IOM' : selectedGuidelineDetails?.name || 'Guidelines'}
            </Box>{' '}
            baseline reference for {age != null ? `a ${age}-year-old` : 'an adult'} {gender}.
            Condition-specific adjustments are shown in Client Targets.
          </Typography>
        </Paper>
      </Box>

        <Box sx={{ minWidth: 0, overflowX: 'auto' }}>
          {isLoadingDrv ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <NutritionReportTable
              analysisData={analysisData}
              clientDisplayName={clientDisplayName}
              subtitleParts={subtitleParts}
              variant="suggested-rda"
              primaryTypes={primaryTypes}
              client={client}
              drvRequirements={activeDrvRequirements}
            />
          )}
        </Box>
    </Box>
  );
};
